import React, { useEffect, useState } from "react";
import { memberApi } from "@/lib/memberAuth";
import { toast } from "sonner";
import { ShieldWarning, Plus } from "@phosphor-icons/react";

export default function MemberPayments() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    shipment_reference: "", counterparty_company: "", amount_disputed: "",
    currency: "EUR", description: "",
  });

  const load = () => {
    setLoading(true);
    memberApi.get("/portal/payments/incidents")
      .then(({ data }) => setIncidents(data.incidents || []))
      .catch(() => toast.error("Could not load incidents"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.counterparty_company || !form.amount_disputed) {
      toast.error("Counterparty and amount are required.");
      return;
    }
    try {
      await memberApi.post("/portal/payments/incidents", {
        ...form,
        amount_disputed: parseFloat(form.amount_disputed),
      });
      toast.success("Incident opened. EPAS operations will follow up within one business day.");
      setForm({ shipment_reference: "", counterparty_company: "", amount_disputed: "", currency: "EUR", description: "" });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not open incident");
    }
  };

  return (
    <div data-testid="portal-payments">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-1">Portal</div>
          <h1 className="font-display text-3xl font-bold text-navy tracking-tight flex items-center gap-2">
            <ShieldWarning size={24} weight="duotone" className="text-ember" /> Payment Monitor
          </h1>
          <p className="text-sm text-navy/70 mt-2 max-w-2xl">
            Open a Payment Protection incident when a counterparty is overdue, disputing, or non-communicative.
            EPAS mediates. Attach evidence via the portal — never by email.
          </p>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="payments-open-incident-btn"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest bg-navy text-white px-3 py-2 hover:bg-navy/90">
          <Plus size={12} /> Open incident
        </button>
      </header>

      {showForm && (
        <form onSubmit={handleSubmit} data-testid="payments-form" className="border border-black/10 bg-white p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block text-xs uppercase tracking-widest text-navy/60">Shipment reference
            <input value={form.shipment_reference} onChange={(e) => setForm({ ...form, shipment_reference: e.target.value })} className="input mt-1" data-testid="payments-shipment-ref" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-navy/60">Counterparty company *
            <input value={form.counterparty_company} onChange={(e) => setForm({ ...form, counterparty_company: e.target.value })} className="input mt-1" data-testid="payments-counterparty" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-navy/60">Amount disputed *
            <input value={form.amount_disputed} onChange={(e) => setForm({ ...form, amount_disputed: e.target.value })} className="input mt-1" type="number" step="0.01" data-testid="payments-amount" />
          </label>
          <label className="block text-xs uppercase tracking-widest text-navy/60">Currency
            <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="input mt-1">
              <option>EUR</option><option>USD</option><option>GBP</option><option>CHF</option>
            </select>
          </label>
          <label className="block text-xs uppercase tracking-widest text-navy/60 md:col-span-2">Describe the situation
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input mt-1 min-h-[110px]" data-testid="payments-description" />
          </label>
          <div className="md:col-span-2 flex gap-2">
            <button type="submit" className="bg-navy text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-navy/90" data-testid="payments-submit">Open incident</button>
            <button type="button" onClick={() => setShowForm(false)} className="border border-black/20 px-4 py-2 text-xs uppercase tracking-widest">Cancel</button>
          </div>
        </form>
      )}

      <section data-testid="payments-list" className="space-y-3">
        {loading ? (
          <div className="border border-black/10 bg-white p-6 text-center text-navy/50 text-sm">Loading…</div>
        ) : incidents.length === 0 ? (
          <div className="border border-black/10 bg-white p-8 text-center text-navy/50 text-sm">
            No open incidents. Payment Protection is standing by — you'll open your first when you need it.
          </div>
        ) : incidents.map((inc) => (
          <div key={inc.id} className="border border-black/10 bg-white p-5" data-testid={`incident-${inc.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-widest text-navy/50">Incident · {inc.status}</div>
                <div className="font-display font-bold text-navy text-lg mt-1">{inc.counterparty_company}</div>
                <div className="text-sm text-navy/70 mt-1">
                  {inc.shipment_reference && <>Shipment {inc.shipment_reference} · </>}
                  {inc.currency} {Number(inc.amount_disputed).toLocaleString()}
                </div>
              </div>
              <div className="text-xs text-navy/50">{new Date(inc.created_at).toLocaleDateString()}</div>
            </div>
            {inc.description && <p className="text-sm text-navy/80 mt-3 whitespace-pre-wrap">{inc.description}</p>}
            {inc.timeline?.length > 0 && (
              <div className="mt-4 border-t border-black/5 pt-3">
                <div className="overline mb-2">Timeline</div>
                <ul className="text-xs text-navy/70 space-y-1">
                  {inc.timeline.map((t, i) => (
                    <li key={i}>· <span className="text-navy/50">{new Date(t.ts).toLocaleString()}</span> — {t.actor}: {t.note}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}
